<template>
  <div ref="wrapperRef" class="m-filedownload-wrapper">
    <div class="m-filedownload-container">
      <InputText
        v-model="fileName"
        :disabled="true"
        :placeholder="placeholder"
        class="m-filedownload-input"
        readonly
      />
      
      <Button
        icon="pi pi-download"
        :label="downloadLabel"
        :disabled="disabled || !hasFile"
        class="m-filedownload-button"
        @click="handleDownload"
        :title="downloadTitle"
      />

      <Button
        v-if="canOpen"
        icon="pi pi-external-link"
        :disabled="disabled || !hasFile"
        class="m-filedownload-open-button"
        @click="handleOpen"
        title="Открыть файл"
      />
    </div>

    <div v-if="hint" class="m-filedownload-hint">
      {{ hint }}
    </div>

    <FieldVerifyErrorOverlay :messages="normalizedErrors" :anchor-el="wrapperRef" />
  </div>
</template>

<script setup lang="ts">
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import FieldVerifyErrorOverlay from '@/components/ui/FieldVerifyErrorOverlay.vue'
import { computed, ref } from 'vue'

interface Props {
  modelValue?: { 
    fileName?: string
    fileUrl?: string
    fileData?: string | Blob
  } | null
  downloadLabel?: string
  downloadTitle?: string
  placeholder?: string
  hint?: string
  disabled?: boolean
  canOpen?: boolean
  errorMessages?: string | string[]
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  downloadLabel: '',
  downloadTitle: 'Скачать файл',
  placeholder: 'Файл не выбран',
  hint: '',
  disabled: false,
  canOpen: true,
  errorMessages: () => []
})

const emit = defineEmits<{
  'download': []
  'open': []
}>()

const wrapperRef = ref<HTMLElement | null>(null)
const fileName = computed(() => props.modelValue?.fileName || '')
const hasFile = computed(() => !!props.modelValue?.fileName)

const normalizedErrors = computed(() => {
  if (Array.isArray(props.errorMessages)) return props.errorMessages
  return [props.errorMessages]
})

const handleDownload = () => {
  if (!props.modelValue) return

  emit('download')

  // If fileUrl is provided, open it
  if (props.modelValue.fileUrl) {
    const link = document.createElement('a')
    link.href = props.modelValue.fileUrl
    link.download = props.modelValue.fileName || 'download'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } 
  // If fileData is provided, create blob and download
  else if (props.modelValue.fileData) {
    const blob = props.modelValue.fileData instanceof Blob 
      ? props.modelValue.fileData 
      : new Blob([props.modelValue.fileData])
    
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = props.modelValue.fileName || 'download'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }
}

const handleOpen = () => {
  if (!props.modelValue) return

  emit('open')

  // Open file in new tab
  if (props.modelValue.fileUrl) {
    window.open(props.modelValue.fileUrl, '_blank')
  }
}
</script>

<style scoped lang="scss">
.m-filedownload-wrapper {
  width: 100%;
}

.m-filedownload-container {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.m-filedownload-input {
  flex: 1;
}

.m-filedownload-button,
.m-filedownload-open-button {
  flex-shrink: 0;
}

.m-filedownload-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.5rem;
}
</style>

